import { Engine, EngineOptions, Scene } from '@babylonjs/core';
import {
  BaseEngine,
  BaseEngineOptions,
  ENGINE_OPTIONS_TOKEN,
  OrganizedSystemOperations,
  SYSTEMS_TOKEN,
} from '@modules/core/src/injectable';
import { Zed } from '@modules/core/src/Zed';
import { World } from 'miniplex';

import { checkDeviceTier, DeviceInfo, DeviceTier } from './DeviceInfo';
import { QualitySettings, Resolution } from './QualitySettings';
import { SystemManager } from './SystemManager';
import { CanvasSystem } from './systems/CanvasSystem';

export const BABYLONJS_MODULE_OPTIONS_TOKEN = Symbol.for('BABYLONJS_MODULE_OPTIONS_TOKEN');
export type BabylonjsModuleOptions = {
  apiEndpoint: string;
  assetsEndpoint: string;
  antialiasing?: boolean;
  engineOptions?: EngineOptions;
  metadataVersion?: string;
  deviceInfo?: DeviceInfo;
  deviceTier?: DeviceTier;
  qualitySettings: QualitySettings;
  powerPreference?: string;
};

let qualitySettings: QualitySettings = {
  modelLOD: 'LOD0',
  resolution: Resolution.p900,
  reflection: false,
  textureOption: {
    textureMaxSize: '2048',
    useBaseColorTexture: true,
    useMetallicRoughnessTexture: false,
    useNormalTexture: false,
    useAmbientOcclusionTexture: true,
    useLightmapTexture: true,
    useEmissiveTexture: false,
    useReflectionProbe: true,
  },
};

export const getQualitySettings = () => qualitySettings;

export class BabylonjsModule implements BaseEngine {
  static readonly INJECTABLE_TOKEN = Symbol.for('BabylonjsModule');
  static readonly SCENE_TOKEN = Symbol.for('BabylonjsModule.Scene');

  static readonly [SYSTEMS_TOKEN] = [CanvasSystem];

  readonly options: BaseEngineOptions & BabylonjsModuleOptions;

  readonly api;

  readonly canvas;
  readonly engine;
  readonly scene;

  systemManager?: SystemManager;

  constructor({
    [Zed.WORLD_TOKEN]: _world,
    [ENGINE_OPTIONS_TOKEN]: baseOptions,
    [BABYLONJS_MODULE_OPTIONS_TOKEN]: options,
  }: {
    [Zed.WORLD_TOKEN]: World;
    [ENGINE_OPTIONS_TOKEN]: BaseEngineOptions;
    [BABYLONJS_MODULE_OPTIONS_TOKEN]: BabylonjsModuleOptions;
  }) {
    if (options.deviceInfo) {
      options.deviceTier = checkDeviceTier(options.deviceInfo.modelNumber, options.deviceInfo.deviceMemory);
      console.log(`set deviceTier : ${options.deviceTier}`);
    }

    this.options = {
      ...baseOptions,
      ...options,
    };

    this.canvas = document.createElement('canvas');    
    this.engine = new Engine(this.canvas, this.options.antialiasing ?? true, {
      adaptToDeviceRatio: false,
      powerPreference: 'low-power',
      ...this.options.engineOptions,
    });
    // 모바일에서 이 값을 수동으로 true로 하지 않으면, 첫 렌더링이 느려진다 해 추가
    // https://forum.babylonjs.com/t/engine-disableuniformbuffers-is-set-to-false-in-mobile-mode-and-cause-a-bad-performance/41228
    this.engine.disableUniformBuffers = true;
    this.engine.onAfterViewRenderObservable;

    this.scene = new Scene(this.engine);

    this.api = {
      engine: this.engine,
      scene: this.scene,
    };
  }

  registerSystemOperations(systemOperations: OrganizedSystemOperations): void {
    this.systemManager = new SystemManager(this.engine, this.scene, systemOperations);
  }

  dispose() {
    this.scene.clearCachedVertexData();
    this.scene.cleanCachedTextureBuffer();
    this.scene.dispose();
    this.engine.dispose();
  }
}
